import { useState, useMemo, useEffect } from 'react';
import { Search, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { PriceDisplay } from './PriceDisplay';
import { DiscountBadge } from './DiscountBadge';
import { supabase } from '@/integrations/supabase/client';
import { ImagePresets } from '@/lib/image-optimizer';
import { useLanguage } from '@/contexts/LanguageContext';

interface Product {
  id: string;
  name: string;
  brand: string;
  category?: string;
  price: number;
  skin_type: string;
  image: string;
  is_new: boolean;
  is_bestseller: boolean;
  recommended_by_cosmetologists?: boolean;
  rating: number;
  reviews: number;
  show_price_to_unregistered?: boolean;
  discount_percentage?: number;
  description?: string;
  ingredients?: string;
  requires_consultation?: boolean;
  hide_price_without_consultation?: boolean;
  categoriesNames?: string[];
}

interface SearchModalProps {
  children: React.ReactNode;
}

// Helper function to normalize Lithuanian characters to ASCII equivalents
const normalizeLithuanian = (text: string): string => {
  const lithuanianMap: Record<string, string> = {
    'ą': 'a', 'Ą': 'A',
    'č': 'c', 'Č': 'C',
    'ę': 'e', 'Ę': 'E',
    'ė': 'e', 'Ė': 'E',
    'į': 'i', 'Į': 'I',
    'š': 's', 'Š': 'S',
    'ų': 'u', 'Ų': 'U',
    'ū': 'u', 'Ū': 'U',
    'ž': 'z', 'Ž': 'Z'
  };
  
  return text.split('').map(char => lithuanianMap[char] || char).join('');
};

const SearchModal = ({ children }: SearchModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { t } = useLanguage();

  // Load products and categories from database when modal opens
  useEffect(() => {
    if (isOpen) {
      loadProducts();
      loadCategories();
    }
  }, [isOpen]);

const loadProducts = async () => {
  try {
    setLoading(true);
    const { data: productsData, error: productsError } = await supabase
      .from('products')
      .select('id, name, brand, price, skin_type, image, is_new, is_bestseller, recommended_by_cosmetologists, rating, reviews, show_price_to_unregistered, discount_percentage, description, ingredients, requires_consultation, hide_price_without_consultation')
      .eq('is_inactive', false);

    if (productsError) {
      console.error('Error loading products:', productsError);
      return;
    }

    // Load product categories mapping
    const { data: pcData, error: pcError } = await supabase
      .from('product_categories')
      .select('product_id, categories:categories(name)');

    if (pcError) {
      console.error('Error loading product categories:', pcError);
    }

    const categoryMap = (pcData || []).reduce((acc: Record<string, string[]>, row: any) => {
      const pid = row.product_id;
      const name = row.categories?.name as string | undefined;
      if (!pid || !name) return acc;
      if (!acc[pid]) acc[pid] = [];
      if (!acc[pid].includes(name)) acc[pid].push(name);
      return acc;
    }, {});

    setProducts((productsData || []).map(p => ({
      ...p,
      categoriesNames: categoryMap[p.id] || [],
    })));
  } catch (error) {
    console.error('Error loading products:', error);
  } finally {
    setLoading(false);
  }
};

  const loadCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('name')
        .eq('is_active', true)
        .eq('show_in_menu', true)
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('Error loading categories:', error);
        return;
      }

      setCategories(data?.map(c => c.name) || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  // Order-independent search with word matching and relevance scoring
  const filteredResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    
    const query = normalizeLithuanian(searchQuery.toLowerCase().trim());
    const queryWords = query.split(/\s+/).filter(word => word.length > 0);
    
    return products
      .map(product => {
        let score = 0;
        const categoryNames = (product.categoriesNames || []).map(c => normalizeLithuanian(c.toLowerCase()));
        
        const searchableFields = {
          name: normalizeLithuanian(product.name.toLowerCase()),
          brand: normalizeLithuanian(product.brand.toLowerCase()),
          categories: categoryNames.join(' '),
          skin_type: normalizeLithuanian(product.skin_type.toLowerCase()),
          description: normalizeLithuanian((product.description || '').toLowerCase()),
          ingredients: normalizeLithuanian((product.ingredients || '').toLowerCase())
        };
        
        const allSearchableText = Object.values(searchableFields).join(' ');
        
        // Check how many query words match
        let matchedWords = 0;
        queryWords.forEach(word => {
          // Check if any category name contains the word or starts with it
          const categoryMatch = categoryNames.some(cat => 
            cat.includes(word) || cat.startsWith(word) || word.startsWith(cat)
          );
          
          // Exact field matches get higher scores
          if (searchableFields.name.includes(word)) {
            score += 10;
            matchedWords++;
          } else if (searchableFields.brand.includes(word)) {
            score += 8;
            matchedWords++;
          } else if (categoryMatch) {
            score += 6;
            matchedWords++;
          } else if (searchableFields.skin_type.includes(word)) {
            score += 4;
            matchedWords++;
          } else if (searchableFields.description.includes(word)) {
            score += 3;
            matchedWords++;
          } else if (searchableFields.ingredients.includes(word)) {
            score += 3;
            matchedWords++;
          } else if (allSearchableText.includes(word)) {
            score += 2;
            matchedWords++;
          }
        });
        
        // Bonus points for matching all words (order-independent complete match)
        if (matchedWords === queryWords.length && queryWords.length > 1) {
          score += 15;
        }
        
        // Bonus for exact phrase match (original behavior)
        if (allSearchableText.includes(query)) {
          score += 5;
        }
        
        return { ...product, searchScore: score, matchedWords };
      })
      .filter(product => product.matchedWords > 0) // Must match at least one word
      .sort((a, b) => b.searchScore - a.searchScore)
      .slice(0, 50); // Limit to 50 results
  }, [searchQuery, products]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="w-[calc(100vw-2rem)] sm:w-[95vw] max-w-3xl h-[85vh] max-h-[700px] p-0 flex flex-col">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle>{t.searchTitle}</DialogTitle>
          <DialogDescription>
            {t.searchDescription}
          </DialogDescription>
        </DialogHeader>
        
        
        <div className="flex-1 flex flex-col min-h-0 p-6 pt-4">
          <div className="flex-shrink-0 mb-6">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
              <Input
                placeholder={t.searchProducts}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-20 h-12 text-base border-2 focus:border-primary"
                autoFocus
              />
              {searchQuery && (
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-muted-foreground text-sm bg-muted px-2 py-1 rounded">
                  {filteredResults.length} {t.results}
                </div>
              )}
            </div>
          </div>
          
          {loading && (
            <div className="text-center py-8 text-muted-foreground">
              {t.searching}
            </div>
          )}
          
          {searchQuery && !loading && (
            <div className="flex-1 min-h-0 overflow-hidden">
              <div className="h-full overflow-y-auto space-y-4 pr-2">
              {filteredResults.length > 0 ? (
                filteredResults.map((product) => (
                  <div
                    key={product.id}
                    className="flex items-start gap-4 p-5 min-h-[120px] rounded-xl border border-border hover:border-primary/50 hover:bg-muted/50 cursor-pointer transition-all duration-200 group overflow-hidden"
                    onClick={() => {
                      setIsOpen(false);
                      setSearchQuery('');
                      navigate(`/product/${product.id}`);
                    }}
                  >
                    <img
                      src={ImagePresets.icon(product.image)}
                      alt={product.name}
                      className="w-24 h-24 object-cover rounded-xl flex-shrink-0 shadow-sm"
                      onError={(e) => {
                        try {
                          const fileName = (product.image || '').split('/').pop() || '';
                          const { data } = supabase.storage.from('product-images').getPublicUrl(fileName);
                          if (data?.publicUrl) {
                            e.currentTarget.src = ImagePresets.icon(data.publicUrl);
                            e.currentTarget.onerror = null;
                          }
                        } catch {}
                      }}
                    />
                    
                    <div className="flex-1 min-w-0 flex flex-col h-full">
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="flex-1">
                            <div className="flex items-start gap-2 mb-2">
                              <h4 className="font-semibold text-base leading-tight group-hover:text-primary transition-colors flex-1">
                                {product.name}
                              </h4>
                              <div className="flex gap-1 flex-shrink-0">
                                {product.is_new && (
                                  <Badge variant="secondary" className="text-xs">{t.productDetail.new}</Badge>
                                )}
                                {product.recommended_by_cosmetologists && (
                                  <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg border-0 text-xs">
                                    ★ {t.productDetail.recommended}
                                  </Badge>
                                )}
                                {product.discount_percentage > 0 && (
                                  <DiscountBadge 
                                    discountPercentage={product.discount_percentage} 
                                    size="sm"
                                  />
                                )}
                              </div>
                            </div>
                            
<p className="text-sm text-muted-foreground leading-relaxed">
  {product.brand} • {(product.categoriesNames && product.categoriesNames.length > 0 ? product.categoriesNames.join(', ') : t.noCategory)}
</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-auto space-y-3">
                        <div className="text-center">
                          <PriceDisplay 
                            price={Number(product.price)}
                            discountPercentage={product.discount_percentage}
                            className="font-bold text-lg"
                            showLoginPrompt={false}
                            showPriceToUnregistered={product.show_price_to_unregistered}
                            hidePriceWithoutConsultation={product.hide_price_without_consultation}
                            requiresConsultation={product.requires_consultation}
                            hasConsultationApproval={false} // TODO: Check consultation approval status
                          />
                        </div>
                        
                        <Button
                          size="sm"
                          className="w-full h-10 font-medium shadow-sm hover:shadow-md transition-shadow"
                          onClick={(e) => {
                            e.stopPropagation();
                            addToCart({
                              id: product.id,
                              name: product.name,
                              brand: product.brand,
                              price: Number(product.price),
                              image: product.image,
                              discount_percentage: product.discount_percentage || 0
                            });
                          }}
                        >
                          <ShoppingCart size={16} className="mr-2" />
                          {t.addToCart}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Search size={48} className="mx-auto mb-4 opacity-50" />
                  <p>{t.noResultsFound}</p>
                  <p className="text-sm mt-1">{t.tryDifferentKeywords}</p>
                </div>
              )}
              </div>
            </div>
          )}
          
          {!searchQuery && !loading && (
            <div className="flex-1 min-h-0">
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium mb-3">{t.popularSearches}</h4>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { key: 'faceSerum', label: t.popularSearchTerms.faceSerum },
                      { key: 'moisturizingCream', label: t.popularSearchTerms.moisturizingCream },
                      { key: 'eyeCream', label: t.popularSearchTerms.eyeCream },
                      { key: 'micellarWater', label: t.popularSearchTerms.micellarWater },
                      { key: 'toner', label: t.popularSearchTerms.toner },
                    ].map((term) => (
                      <Button
                        key={term.key}
                        variant="secondary"
                        size="sm"
                        onClick={() => setSearchQuery(term.label)}
                        className="text-xs"
                      >
                        {term.label}
                      </Button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium mb-3">{t.browseCategories}</h4>
                  <div className="flex flex-wrap gap-2">
                    {categories.map((category) => (
                      <Button
                        key={category}
                        variant="secondary"
                        size="sm"
                        onClick={() => setSearchQuery(category)}
                        className="text-xs"
                      >
                        {category}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchModal;